import React, { useState } from "react";
import { graphql, navigate, useStaticQuery } from "gatsby";

import {
    layout,
    content,
    auth,
    authPhoto,
    authImage,
    authColumn,
    authIntro,
    authEyebrow,
    authDivider,
    authDividerCompact,
    authHeroTitle,
    authFeatures,
    authFeature,
    authFeatureIcon,
    titleText,
    contactLink,
    contactButton,
    authNav,
    brand,
    brandLogo,
    navLinks,
    loginButton,
    menuToggle,
    mobileMenu,
    mobileMenuOpen,
} from "./auth-layout.module.scss";
import logoFull from "../assets/images/logo_full.png";
import loveArrow from "../assets/icons/love-arrow.svg";
import ClockIcon from "../assets/images/svg/clock.svg";
import MannequinIcon from "../assets/images/svg/mannequin.svg";
import StarIcon from "../assets/images/svg/star.svg";
import { ISEOProps } from "../components/seo";

import MainLayout from "./main-layout";
import Link from "../components/atoms/link";
import Title, { ITitleProps } from "../components/atoms/title";
import useUser from "../apps/auth/hooks/use-user";
import { getAbsolutePath } from "../apps/utility/routes.utility";

const navItems = [
    { label: "O Mnie", to: "/o-nas/" },
    { label: "Blog", to: "/blog/" },
    { label: "Kontakt", to: "/kontakt/" },
];

export interface IAuthProps {
    className?: string;
    contentClassName?: string;
    title?: string;
    titleAlign?: ITitleProps["align"];
    titleSize?: ITitleProps["size"];
    SEOProps?: ISEOProps;
}

const AuthLayout: React.FC<IAuthProps> = ({
    children,
    className,
    contentClassName,
    title,
    titleAlign,
    titleSize = "large",
    SEOProps = {},
}) => {
    const { loginImageData } = useStaticQuery(query);
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const { isLoggedIn } = useUser();
    const accountLinkHref = getAbsolutePath(isLoggedIn ? "ACCOUNT_DASHBOARD" : "AUTH_LOGIN");
    const accountLinkLabel = isLoggedIn ? "Twoje konto" : "Zaloguj się";

    return (
        <MainLayout
            className={layout}
            HeaderComponent={null}
            footer={false}
            SEOProps={{
                title: title,
                ...SEOProps,
            }}
        >
            <nav className={authNav} aria-label="Główna nawigacja">
                <Link to="/" className={brand}>
                    <img
                        src={logoFull}
                        alt="Fashion Fairy Virtual Stylist"
                        className={brandLogo}
                    />
                </Link>
                <div className={navLinks}>
                    {navItems.map(item => (
                        <Link key={item.to} to={item.to} noPrefetch>
                            {item.label}
                        </Link>
                    ))}
                </div>
                <Link to={accountLinkHref} className={loginButton} noPrefetch>
                    {accountLinkLabel}
                </Link>
                <button
                    className={menuToggle}
                    type="button"
                    aria-label={isMenuOpen ? "Zamknij menu" : "Otwórz menu"}
                    aria-expanded={isMenuOpen}
                    aria-controls="auth-mobile-menu"
                    onClick={() => setIsMenuOpen(isOpen => !isOpen)}
                >
                    <span />
                    <span />
                    <span />
                </button>
                <div
                    id="auth-mobile-menu"
                    className={`${mobileMenu} ${isMenuOpen ? mobileMenuOpen : ""}`}
                >
                    {navItems.map(item => (
                        <Link key={item.to} to={item.to} noPrefetch>
                            {item.label}
                        </Link>
                    ))}
                    <button
                        type="button"
                        onClick={() => {
                            setIsMenuOpen(false);
                            navigate(accountLinkHref);
                        }}
                    >
                        {accountLinkLabel}
                    </button>
                </div>
            </nav>
            <div className={auth}>
                <section className={authPhoto}>
                    {loginImageData?.publicURL && (
                        <img
                            src={loginImageData.publicURL}
                            alt=""
                            className={authImage}
                            aria-hidden="true"
                        />
                    )}
                    <div className={authIntro}>
                        <p className={authEyebrow}>Witaj ponownie w świecie Fashion Fairy</p>
                        <span
                            className={`${authDivider} ${authDividerCompact}`}
                            aria-hidden="true"
                        >
                            <img src={loveArrow} alt="" />
                        </span>
                        <h1 className={authHeroTitle}>
                            <span>Najlepsze stylizacje zaczynają się</span>
                            <strong>od jednego kliknięcia.</strong>
                        </h1>
                        <span className={authDivider} aria-hidden="true">
                            <img src={loveArrow} alt="" />
                        </span>
                        <div className={authFeatures}>
                            <div className={authFeature}>
                                <span className={authFeatureIcon}>
                                    <ClockIcon />
                                </span>
                                <div>
                                    <strong>Oszczędność czasu</strong>
                                    <span>Gotowe stylizacje w kilka minut.</span>
                                </div>
                            </div>
                            <div className={authFeature}>
                                <span className={authFeatureIcon}>
                                    <MannequinIcon />
                                </span>
                                <div>
                                    <strong>Twój styl, Twoja magia</strong>
                                    <span>My zajmiemy się resztą.</span>
                                </div>
                            </div>
                            <div className={authFeature}>
                                <span className={authFeatureIcon}>
                                    <StarIcon />
                                </span>
                                <div>
                                    <strong>Inspiracje na co dzień</strong>
                                    <span>Trendy, porady i stylizacje na każdą okazję.</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>
                <section className={authColumn}>
                    <main className={className}>
                        {title && (
                            <Title
                                size={titleSize}
                                className={titleText}
                                fontStyle="italic"
                                align={titleAlign}
                            >
                                <span>{title}</span>
                            </Title>
                        )}
                        <div className={`${content} ${contentClassName}`}>
                            {children}
                            <div className={contactLink}>
                                Potrzebujesz pomocy?
                                <Link to="/kontakt/" className={contactButton}>
                                    Napisz do nas!
                                </Link>
                            </div>
                        </div>
                    </main>
                </section>
            </div>
        </MainLayout>
    );
};

const query = graphql`
    query {
        loginImageData: file(relativePath: { eq: "auth/hero_auth.jpeg" }) {
            publicURL
        }
    }
`;

export default AuthLayout;
